import { z } from 'zod';
import {
    VersionedArtifactTypeGroup,
    VersionedArtifactTypeGroupSchema,
} from '../enumerations/VersionedArtifactTypeGroup';
import { defaultVersionInfo, VersionInfoSchema } from '../services/versionInfo';
import { IdSchema } from './baseSchemas';
import { msObject } from './msObject';
import {
    defaultNamedParentedPersistableResource,
    defaultNamedPersistableResource,
    defaultParentedPersistableResource,
    defaultPersistableResource,
    getMultiParentedPersistableResourceSchemaObject,
    NamedParentedPersistableResourceSchemaObject,
    NamedPersistableResourceSchemaObject,
    ParentedPersistableResourceSchemaObject,
    PersistableResourceSchemaObject,
    PersistableSchemaType,
} from './persistableResourceTypes';

const ArtifactResourceSchemaObject = {
    ...PersistableResourceSchemaObject,
    objectId: IdSchema,
};
export const ArtifactResourceSchema = msObject(ArtifactResourceSchemaObject);

const NamedArtifactResourceSchemaObject = {
    ...NamedPersistableResourceSchemaObject,
    ...ArtifactResourceSchemaObject,
};
export const NamedArtifactResourceSchema = msObject(NamedArtifactResourceSchemaObject);

// -- Parented

const ParentedArtifactResourceSchemaObject = {
    ...ParentedPersistableResourceSchemaObject,
    ...ArtifactResourceSchemaObject,
};
export const ParentedArtifactResourceSchema = msObject(ParentedArtifactResourceSchemaObject);

const NamedParentedArtifactResourceSchemaObject = {
    ...NamedParentedPersistableResourceSchemaObject,
    ...NamedArtifactResourceSchemaObject,
    ...ParentedArtifactResourceSchemaObject,
};
export const NamedParentedArtifactResourceSchema = msObject(NamedParentedArtifactResourceSchemaObject);

// -- MultiParented

const getMultiParentedArtifactResourceSchemaObject = <E extends string>(parentRelationSchema: z.ZodType<E, E>) => ({
    ...ParentedArtifactResourceSchemaObject,
    ...getMultiParentedPersistableResourceSchemaObject(parentRelationSchema),
});
export const createMultiParentedArtifactResourceSchema = <E extends string>(parentRelationSchema: z.ZodType<E, E>) =>
    msObject(getMultiParentedArtifactResourceSchemaObject(parentRelationSchema));

const getNamedMultiParentedArtifactResourceSchemaObject = <E extends string>(
    parentRelationSchema: z.ZodType<E, E>
) => ({
    ...NamedParentedArtifactResourceSchemaObject,
    ...getMultiParentedArtifactResourceSchemaObject(parentRelationSchema),
});
export const createNamedMultiParentedArtifactResourceSchema = <E extends string>(
    parentRelationSchema: z.ZodType<E, E>
) => msObject(getNamedMultiParentedArtifactResourceSchemaObject(parentRelationSchema));

// -- Versioned

const VersionedArtifactResourceSchemaObject = {
    ...ParentedArtifactResourceSchemaObject,
    versionInfo: VersionInfoSchema,
};
export const VersionedArtifactResourceSchema = msObject(VersionedArtifactResourceSchemaObject);

const getVersionedMultiParentedArtifactResourceSchemaObject = <E extends string>(
    parentRelationSchema: z.ZodType<E, E>
) => ({
    ...VersionedArtifactResourceSchemaObject,
    ...getMultiParentedArtifactResourceSchemaObject(parentRelationSchema),
});
export const createVersionedMultiParentedArtifactResourceSchema = <E extends string>(
    parentRelationSchema: z.ZodType<E, E>
) => msObject(getVersionedMultiParentedArtifactResourceSchemaObject(parentRelationSchema));

// -- IndirectlyVersioned

const IndirectlyVersionedArtifactResourceSchemaObject = {
    ...ParentedArtifactResourceSchemaObject,
    versionedIn: IdSchema,
};
export const IndirectlyVersionedArtifactResourceSchema = msObject(IndirectlyVersionedArtifactResourceSchemaObject);

const IndirectlyVersionedAndNamedArtifactResourceSchemaObject = {
    ...NamedParentedArtifactResourceSchemaObject,
    ...IndirectlyVersionedArtifactResourceSchemaObject,
};
export const IndirectlyVersionedAndNamedArtifactResourceSchema = msObject(
    IndirectlyVersionedAndNamedArtifactResourceSchemaObject
);

const getIndirectlyVersionedMultiParentedArtifactResourceSchemaObject = <E extends string>(
    parentRelationSchema: z.ZodType<E, E>
) => ({
    ...IndirectlyVersionedArtifactResourceSchemaObject,
    ...getMultiParentedArtifactResourceSchemaObject(parentRelationSchema),
});
export const createIndirectlyVersionedMultiParentedArtifactResourceSchema = <E extends string>(
    parentRelationSchema: z.ZodType<E, E>
) => msObject(getIndirectlyVersionedMultiParentedArtifactResourceSchemaObject(parentRelationSchema));

const getIndirectlyVersionedAndNamedMultiParentedArtifactResourceSchemaObject = <E extends string>(
    parentRelationSchema: z.ZodType<E, E>
) => ({
    ...IndirectlyVersionedAndNamedArtifactResourceSchemaObject,
    ...getIndirectlyVersionedMultiParentedArtifactResourceSchemaObject(parentRelationSchema),
    ...getNamedMultiParentedArtifactResourceSchemaObject(parentRelationSchema),
});
export const createIndirectlyVersionedAndNamedMultiParentedArtifactResourceSchema = <E extends string>(
    parentRelationSchema: z.ZodType<E, E>
) => msObject(getIndirectlyVersionedAndNamedMultiParentedArtifactResourceSchemaObject(parentRelationSchema));

const IndirectlyVersionedDefaultVariantArtifactResourceSchemaObject = {
    ...IndirectlyVersionedArtifactResourceSchemaObject,
    variant: IdSchema,
};
export const IndirectlyVersionedDefaultVariantArtifactResourceSchema = msObject(
    IndirectlyVersionedDefaultVariantArtifactResourceSchemaObject
);

// -- MultiVersioned

const IndirectlyMultiVersionedArtifactResourceSchemaObject = {
    ...IndirectlyVersionedArtifactResourceSchemaObject,
    versionedInArtifactTypeGroup: VersionedArtifactTypeGroupSchema,
};
export const IndirectlyMultiVersionedArtifactResourceSchema = msObject(
    IndirectlyMultiVersionedArtifactResourceSchemaObject
);

const getIndirectlyMultiVersionedMultiParentedArtifactResourceSchemaObject = <E extends string>(
    parentRelationSchema: z.ZodType<E, E>
) => ({
    ...getMultiParentedArtifactResourceSchemaObject(parentRelationSchema),
    ...IndirectlyMultiVersionedArtifactResourceSchemaObject,
});
export const createIndirectlyMultiVersionedMultiParentedArtifactResourceSchema = <E extends string>(
    parentRelationSchema: z.ZodType<E, E>
) => msObject(getIndirectlyMultiVersionedMultiParentedArtifactResourceSchemaObject(parentRelationSchema));

const getIndirectlyMultiVersionedAndNamedMultiParentedArtifactResourceSchemaObject = <E extends string>(
    parentRelationSchema: z.ZodType<E, E>
) => ({
    ...getIndirectlyMultiVersionedMultiParentedArtifactResourceSchemaObject(parentRelationSchema),
    ...getNamedMultiParentedArtifactResourceSchemaObject(parentRelationSchema),
});
export const createIndirectlyMultiVersionedAndNamedMultiParentedArtifactResourceSchema = <E extends string>(
    parentRelationSchema: z.ZodType<E, E>
) => msObject(getIndirectlyMultiVersionedAndNamedMultiParentedArtifactResourceSchemaObject(parentRelationSchema));

// Types

export type ArtifactResource = z.infer<typeof ArtifactResourceSchema>;
export type NamedArtifactResource = z.infer<typeof NamedArtifactResourceSchema>;
export type ParentedArtifactResource = z.infer<typeof ParentedArtifactResourceSchema>;
export type NamedParentedArtifactResource = z.infer<typeof NamedParentedArtifactResourceSchema>;

// type MultiParentedArtifactResourceType<E extends EnumLike> =
//     ReturnType<typeof createMultiParentedArtifactResourceSchema<E>>
// export type MultiParentedArtifactResource<E extends EnumLike> = z.infer<MultiParentedArtifactResourceType<E>>
//
// type NamedMultiParentedArtifactResourceType<E extends EnumLike> =
//     ReturnType<typeof createNamedMultiParentedArtifactResourceSchema<E>>
// export type NamedMultiParentedArtifactResource<E extends EnumLike> = z.infer<NamedMultiParentedArtifactResourceType<E>>

export type VersionedArtifactResource = z.infer<typeof VersionedArtifactResourceSchema>;

// type VersionedMultiParentedArtifactResourceType<E extends EnumLike> =
//     ReturnType<typeof createVersionedMultiParentedArtifactResourceSchema<E>>
// export type VersionedMultiParentedArtifactResource<E extends EnumLike> = z.infer<VersionedMultiParentedArtifactResourceType<E>>

export type IndirectlyVersionedArtifactResource = z.infer<typeof IndirectlyVersionedArtifactResourceSchema>;
export type IndirectlyVersionedAndNamedArtifactResource = z.infer<
    typeof IndirectlyVersionedAndNamedArtifactResourceSchema
>;

// type IndirectlyVersionedMultiParentedArtifactResourceType<E extends EnumLike> =
//     ReturnType<typeof createIndirectlyVersionedMultiParentedArtifactResourceSchema<E>>
// export type IndirectlyVersionedMultiParentedArtifactResource<E extends EnumLike> =
//     z.infer<IndirectlyVersionedMultiParentedArtifactResourceType<E>>

// type IndirectlyVersionedAndNamedMultiParentedArtifactResourceType<E extends EnumLike> =
//     ReturnType<typeof createIndirectlyVersionedAndNamedMultiParentedArtifactResourceSchema<E>>
// export type IndirectlyVersionedAndNamedMultiParentedArtifactResource<E extends EnumLike> =
//     z.infer<IndirectlyVersionedAndNamedMultiParentedArtifactResourceType<E>>

export type IndirectlyVersionedDefaultVariantArtifactResource = z.infer<
    typeof IndirectlyVersionedDefaultVariantArtifactResourceSchema
>;

export type IndirectlyMultiVersionedArtifactResource = z.infer<typeof IndirectlyMultiVersionedArtifactResourceSchema>;

// type IndirectlyMultiVersionedArtifactResourceType<E extends EnumLike> =
//     ReturnType<typeof createIndirectlyMultiVersionedArtifactResourceSchema<E>>
// export type IndirectlyMultiVersionedArtifactResource<E extends EnumLike> =
//     z.infer<IndirectlyMultiVersionedArtifactResourceType<E>>

// type IndirectlyMultiVersionedAndNamedArtifactResourceType<E extends EnumLike> =
//     ReturnType<typeof createIndirectlyMultiVersionedAndNamedArtifactResourceSchema<E>>
// export type IndirectlyMultiVersionedAndNamedArtifactResource<E extends EnumLike> =
//     z.infer<IndirectlyMultiVersionedAndNamedArtifactResourceType<E>>

export const createGetAllOrSingleObjectSchema = <RS extends PersistableSchemaType<ArtifactResource>>(
    resourceSchema: RS
) =>
    msObject({
        single: resourceSchema.nullable(),
        all: resourceSchema.array().nullable(),
    });

export const defaultArtifactResource: ArtifactResource = {
    ...defaultPersistableResource,
    objectId: '',
} as const;
export const defaultNamedArtifactResource: NamedArtifactResource = {
    ...defaultNamedPersistableResource,
    ...defaultArtifactResource,
} as const;
export const defaultParentedArtifactResource: ParentedArtifactResource = {
    ...defaultParentedPersistableResource,
    ...defaultArtifactResource,
} as const;
export const defaultNamedParentedArtifactResource: NamedParentedArtifactResource = {
    ...defaultNamedParentedPersistableResource,
    ...defaultNamedArtifactResource,
    ...defaultParentedArtifactResource,
} as const;
export const defaultVersionedArtifactResource: VersionedArtifactResource = {
    ...defaultParentedArtifactResource,
    versionInfo: defaultVersionInfo,
} as const;
export const defaultIndirectlyVersionedArtifactResource: IndirectlyVersionedArtifactResource = {
    ...defaultParentedArtifactResource,
    versionedIn: '',
} as const;
export const defaultIndirectlyVersionedAndNamedArtifactResource: IndirectlyVersionedAndNamedArtifactResource = {
    ...defaultIndirectlyVersionedArtifactResource,
    ...defaultNamedParentedArtifactResource,
} as const;
export const defaultIndirectlyVersionedDefaultVariantArtifactResource: IndirectlyVersionedDefaultVariantArtifactResource =
    {
        ...defaultIndirectlyVersionedArtifactResource,
        variant: '',
    } as const;
export const defaultIndirectlyMultiVersionedArtifactResource: IndirectlyMultiVersionedArtifactResource = {
    ...defaultIndirectlyVersionedArtifactResource,
    versionedInArtifactTypeGroup: VersionedArtifactTypeGroup.PROJECT_VERSION,
} as const;
